import { supabase } from "@/integrations/supabase/client";
import { createUuid } from "@/lib/uuid";

const PENDING_APP_TEMPLATE_CONTINUATION_KEY = "applyt.pendingAppTemplateContinuation";
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type PublicAppTemplate = {
  id: string;
  name: string;
  description: string | null;
  published_at: string | null;
  preview_image_url: string | null;
  can_revert: boolean;
  is_snapshot?: boolean;
};

export type HomepageTemplateGallery = {
  homepage_source: "hardcoded" | "catalog";
  templates: PublicAppTemplate[];
};

export type AdminAppTemplateCatalogEntry = PublicAppTemplate & {
  is_visible: boolean;
  display_order: number;
  owner: {
    name: string | null;
    email: string | null;
    role: "Admin" | "Author" | "Member";
  } | null;
};

export type TemplatePublicationRequest = {
  id: string;
  name: string;
  requested_at: string;
  owner: string;
};

export type ReadyForPublicationTemplate = {
  id: string;
  name: string;
  created_at: string;
  owner: string;
};

export type AdminAppTemplateCatalog = {
  homepage_source: "hardcoded" | "catalog";
  templates: AdminAppTemplateCatalogEntry[];
  publication_requests: TemplatePublicationRequest[];
  unpublished_templates: TemplatePublicationRequest[];
  ready_for_publication: ReadyForPublicationTemplate[];
};

export type AppTemplateActionResult = {
  app_id: string;
  job_id: string;
  status: "waiting" | "completed" | "failed";
  ready?: boolean;
  reused?: boolean;
  credit_cost?: number;
};

export type SourceTemplateExportStatus = {
  app_id: string;
  status: "queued" | "running" | "waiting_external_api" | "completed" | "failed" | null;
  job_id: string | null;
  source_unmanaged: boolean;
};

export type AppTemplateProperties = {
  description?: string;
  template?: {
    status?: "preparing_template" | "template" | "retiring_template" | "retire_failed";
    locked?: boolean;
    published_at?: string;
    publication_requested_at?: string;
    snapshot?: boolean;
    public_access_level?: "none" | "reader";
  };
  template_instance?: {
    template_app_id?: string;
    status?: "preparing" | "ready" | "failed";
    source_artifact_status?: "copied" | "unavailable";
  };
};

type AppTemplateFunctionFailure = {
  code: string;
  message: string;
};

export class AppTemplateActionError extends Error {
  readonly code: string;

  constructor(failure: AppTemplateFunctionFailure) {
    super(failure.message);
    this.name = "AppTemplateActionError";
    this.code = failure.code;
  }
}

function functionFailure(data: unknown, fallback: string): AppTemplateFunctionFailure {
  if (!data || typeof data !== "object") return { code: "request_failed", message: fallback };
  const error = (data as { error?: unknown }).error;
  if (!error || typeof error !== "object") return { code: "request_failed", message: fallback };
  const code = (error as { code?: unknown }).code;
  const message = (error as { message?: unknown }).message;
  return {
    code: typeof code === "string" && code.trim() ? code : "request_failed",
    message: typeof message === "string" && message.trim() ? message : fallback,
  };
}

async function functionErrorContext(error: unknown): Promise<unknown> {
  const context = (error as { context?: unknown } | null)?.context as {
    clone?: () => { json?: () => Promise<unknown> };
    json?: () => Promise<unknown>;
  } | undefined;
  try {
    const cloned = context?.clone?.();
    if (cloned?.json) return await cloned.json();
  } catch {
    // Try the original response when cloning is unavailable or already consumed.
  }
  try {
    return context?.json ? await context.json() : null;
  } catch {
    return null;
  }
}

async function invokeTemplateAction<T>(
  body: Record<string, unknown>,
  fallback: string,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke("app-templates", { body });
  if (error) {
    const context = await functionErrorContext(error);
    throw new AppTemplateActionError(functionFailure(context, fallback));
  }
  if ((data as { error?: unknown } | null)?.error) {
    throw new AppTemplateActionError(functionFailure(data, fallback));
  }
  return data as T;
}

export async function loadPublicAppTemplates(): Promise<PublicAppTemplate[]> {
  const data = await invokeTemplateAction<{ templates?: PublicAppTemplate[] }>(
    { action: "catalog" },
    "Templates could not be loaded.",
  );
  return Array.isArray(data.templates) ? data.templates : [];
}

export async function loadHomepageTemplateGallery(): Promise<HomepageTemplateGallery> {
  const data = await invokeTemplateAction<Partial<HomepageTemplateGallery>>(
    { action: "homepage" },
    "Homepage Templates could not be loaded.",
  );
  return {
    homepage_source: data.homepage_source === "catalog" ? "catalog" : "hardcoded",
    templates: Array.isArray(data.templates) ? data.templates : [],
  };
}

export async function loadAdminAppTemplateCatalog(): Promise<AdminAppTemplateCatalog> {
  const data = await invokeTemplateAction<Partial<AdminAppTemplateCatalog>>(
    { action: "admin_catalog" },
    "Template catalog could not be loaded.",
  );
  return {
    homepage_source: data.homepage_source === "catalog" ? "catalog" : "hardcoded",
    templates: Array.isArray(data.templates) ? data.templates : [],
    publication_requests: Array.isArray(data.publication_requests) ? data.publication_requests : [],
    unpublished_templates: Array.isArray(data.unpublished_templates) ? data.unpublished_templates : [],
    ready_for_publication: Array.isArray(data.ready_for_publication) ? data.ready_for_publication : [],
  };
}

export async function saveAdminAppTemplateCatalog(input: {
  expectedTemplateIds: string[];
  templates: Array<Pick<AdminAppTemplateCatalogEntry, "id" | "is_visible">>;
  homepageSource: "hardcoded" | "catalog";
}): Promise<void> {
  await invokeTemplateAction<{ saved?: boolean }>({
    action: "admin_save_catalog",
    expected_template_ids: input.expectedTemplateIds,
    entries: input.templates.map((template) => ({
      template_app_id: template.id,
      is_visible: template.is_visible,
    })),
    homepage_source: input.homepageSource,
  }, "Template catalog could not be saved.");
}

export async function loadAppTemplateStates(
  appIds: string[],
): Promise<Map<string, AppTemplateProperties>> {
  if (appIds.length === 0) return new Map();
  const batches: string[][] = [];
  for (let index = 0; index < appIds.length; index += 200) {
    batches.push(appIds.slice(index, index + 200));
  }
  const responses = await Promise.all(
    batches.map((batch) => invokeTemplateAction<{
      states?: Array<{ app_id: string; properties: AppTemplateProperties }>;
    }>({
      action: "states",
      app_ids: batch,
    }, "App lifecycle could not be loaded.")),
  );
  return new Map(
    responses
      .flatMap((data) => Array.isArray(data.states) ? data.states : [])
      .map((item) => [item.app_id, appTemplateProperties(item.properties)]),
  );
}

export async function loadSourceTemplateExportStatus(appId: string): Promise<SourceTemplateExportStatus> {
  return invokeTemplateAction<SourceTemplateExportStatus>(
    { action: "source_export_status", app_id: appId },
    "Template preparation status could not be loaded.",
  );
}

export async function createAppFromTemplate(
  templateAppId: string,
  requestId: string,
): Promise<AppTemplateActionResult> {
  return await invokeTemplateAction<AppTemplateActionResult>({
    action: "create",
    template_app_id: templateAppId,
    request_id: requestId,
  }, "The App could not be created.");
}

export async function retryTemplateApp(
  appId: string,
  requestId = createUuid(),
): Promise<AppTemplateActionResult> {
  return await invokeTemplateAction<AppTemplateActionResult>({
    action: "retry",
    app_id: appId,
    request_id: requestId,
  }, "The App could not be retried.");
}

export async function convertAppToTemplate(
  appId: string,
  requestId = createUuid(),
): Promise<AppTemplateActionResult> {
  return await invokeTemplateAction<AppTemplateActionResult>({
    action: "convert",
    app_id: appId,
    request_id: requestId,
  }, "The Template could not be prepared.");
}

export async function revertAppTemplate(
  appId: string,
  requestId = createUuid(),
): Promise<AppTemplateActionResult> {
  return await invokeTemplateAction<AppTemplateActionResult>({
    action: "revert",
    app_id: appId,
    request_id: requestId,
  }, "The Template could not be reverted.");
}

export async function publishAppTemplate(appId: string): Promise<{ app_id: string; published: boolean; reused: boolean }> {
  return await invokeTemplateAction({ action: "publish", app_id: appId }, "The Template could not be published.");
}

export async function requestTemplatePublication(appId: string): Promise<{ app_id: string; requested: boolean; reused: boolean }> {
  return await invokeTemplateAction({ action: "request_publication", app_id: appId }, "The publication request could not be sent.");
}

export async function unpublishAppTemplate(appId: string): Promise<{ app_id: string; unpublished: boolean; reused: boolean }> {
  return await invokeTemplateAction({ action: "unpublish", app_id: appId }, "The Template could not be unpublished.");
}

export function templateContinuationPath(templateAppId: string, requestId: string): string {
  const params = new URLSearchParams({
    create: templateAppId,
    request: requestId,
  });
  return `/templates?${params.toString()}`;
}

export function templatePreviewPath(templateAppId: string): string {
  return `/templates?${new URLSearchParams({ template: templateAppId }).toString()}`;
}

export function templatePreviewUrl(templateAppId: string): string {
  const path = templatePreviewPath(templateAppId);
  return typeof window === "undefined" ? path : new URL(path, window.location.origin).toString();
}

export async function copyTemplatePreviewLink(templateAppId: string): Promise<void> {
  const link = templatePreviewUrl(templateAppId);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(link);
      return;
    }
  } catch {
    // Some browser/privacy contexts deny Clipboard API access; use the
    // user-gesture-compatible selection fallback below.
  }

  const textArea = document.createElement("textarea");
  textArea.value = link;
  textArea.setAttribute("readonly", "");
  textArea.style.cssText = "position:fixed;left:-9999px;top:0;opacity:0";
  document.body.appendChild(textArea);
  textArea.select();
  const copied = document.execCommand("copy");
  textArea.remove();
  if (!copied) throw new Error("Clipboard access was denied.");
}

export function normalizeTemplateContinuationPath(value: unknown): string | null {
  if (typeof value !== "string" || value.length > 500 || !value.startsWith("/") || value.startsWith("//")) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(value, "https://applyt.invalid");
  } catch {
    return null;
  }
  if (
    url.origin !== "https://applyt.invalid" ||
    url.pathname !== "/templates" ||
    url.hash ||
    Array.from(url.searchParams.keys()).some((key) => key !== "create" && key !== "request")
  ) {
    return null;
  }
  const templateAppId = url.searchParams.get("create");
  const requestId = url.searchParams.get("request");
  if (
    !templateAppId ||
    !requestId ||
    !UUID_PATTERN.test(templateAppId) ||
    !UUID_PATTERN.test(requestId) ||
    url.searchParams.getAll("create").length !== 1 ||
    url.searchParams.getAll("request").length !== 1
  ) {
    return null;
  }
  return templateContinuationPath(templateAppId, requestId);
}

export function rememberAppTemplateContinuation(value: unknown): string | null {
  const continuation = normalizeTemplateContinuationPath(value);
  if (!continuation || typeof window === "undefined") return continuation;
  try {
    window.localStorage.setItem(PENDING_APP_TEMPLATE_CONTINUATION_KEY, continuation);
  } catch {
    // The invite URL and browser history still carry the durable continuation.
  }
  return continuation;
}

export function pendingAppTemplateContinuation(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return normalizeTemplateContinuationPath(
      window.localStorage.getItem(PENDING_APP_TEMPLATE_CONTINUATION_KEY),
    );
  } catch {
    return null;
  }
}

export function clearAppTemplateContinuation(expected?: string): void {
  if (typeof window === "undefined") return;
  try {
    const current = pendingAppTemplateContinuation();
    if (!expected || current === normalizeTemplateContinuationPath(expected)) {
      window.localStorage.removeItem(PENDING_APP_TEMPLATE_CONTINUATION_KEY);
    }
  } catch {
    // Storage can be unavailable in privacy-restricted browsers.
  }
}

export function appTemplateProperties(value: unknown): AppTemplateProperties {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as AppTemplateProperties
    : {};
}
